import { useCallback, useRef, useState } from "react";
import {
  fetchConversation,
  openRunStream,
  sendApprovalDecision,
  type ChatCallError,
} from "@/features/intelligence/api/client";
import {
  approvalDeliveryOutcome,
  type ApprovalDeliveryOutcome,
} from "@/features/intelligence/lib/approval-frame";
import {
  approvalAnswered,
  hydrateThread,
  shouldReattachAfterDecision,
  type ThreadState,
} from "@/features/intelligence/lib/thread-state";
import type { ApplyThread, DriveStream, MutableRef, StateSetter } from "./types";

export function useApprovalController({
  activeIdRef,
  busyRef,
  threadRef,
  setActionError,
  applyThread,
  drive,
}: {
  activeIdRef: MutableRef<string | null>;
  busyRef: MutableRef<boolean>;
  threadRef: MutableRef<ThreadState>;
  setActionError: StateSetter<ChatCallError | null>;
  applyThread: ApplyThread;
  drive: DriveStream;
}) {
  const [approvalOutcome, setApprovalOutcome] = useState<ApprovalDeliveryOutcome | null>(null);
  const [answeringApproval, setAnsweringApproval] = useState(false);
  const answeringRef = useRef(false);
  const deliveredRef = useRef<Set<string>>(new Set());

  const resetApprovalOutcome = useCallback(() => setApprovalOutcome(null), []);

  const answerApproval = useCallback(
    async (approved: boolean, reason?: string) => {
      const id = activeIdRef.current;
      const state = threadRef.current;
      if (!id || !state.runId || !state.approval || answeringRef.current) return;
      const toolCallId = state.approval.toolCallId;
      setActionError(null);
      answeringRef.current = true;
      setAnsweringApproval(true);
      const trimmed = reason?.trim();
      const result = await sendApprovalDecision(id, state.runId, {
        toolCallId,
        approved,
        ...(trimmed ? { reason: trimmed } : {}),
      }).finally(() => {
        answeringRef.current = false;
        setAnsweringApproval(false);
      });
      if (!result.ok) {
        setActionError(result.error);
        return;
      }
      const outcome = approvalDeliveryOutcome(
        result.value.delivered,
        deliveredRef.current.has(toolCallId),
      );
      setApprovalOutcome(outcome);
      if (outcome === "delivered") {
        deliveredRef.current.add(toolCallId);
        applyThread((current) => approvalAnswered(current, toolCallId));
        const after = threadRef.current;
        const runId = after.runId;
        if (shouldReattachAfterDecision(after, busyRef.current) && runId) {
          await drive(id, (signal) => openRunStream(id, runId, after.lastEventId, signal), {
            quiet404: true,
          });
        }
        return;
      }
      const reread = await fetchConversation(id);
      if (reread.ok && activeIdRef.current === id && !busyRef.current) {
        applyThread((current) => ({
          ...hydrateThread(reread.value),
          notice: current.notice,
          toolStartedAt: current.toolStartedAt,
          healedTools: current.healedTools,
        }));
      }
    },
    [activeIdRef, applyThread, busyRef, drive, setActionError, threadRef],
  );

  return {
    approvalOutcome,
    answeringApproval,
    answerApproval,
    resetApprovalOutcome,
    dismissApprovalOutcome: resetApprovalOutcome,
  };
}
